import { useState } from 'react';
import { useFormik } from 'formik';
import { AlertCircle, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DrawerSection } from '@/components/drawer/DrawerSection';
import { FieldError } from '@/components/form/FieldError';
import { CASH_PAYMENT_TYPES, PaymentTypeField } from '@/components/form/PaymentTypeField';
import { PdfUploadField, type UploadedFile } from '@/components/form/PdfUploadField';
import { getApiErrorMessage } from '@/api/errorHandler';
import { LoanAPIRequestMapper } from '@/api/mapper/loan';
import { createLoan } from '@/api/service/loan';
import type { CashPaymentType } from '@/api/type/payment';
import type { Policy } from '@/types';
import { formatCurrency } from '@/lib/payments';
import { addMonthsToKey, currentMonthKey, formatMonthKey } from '@/lib/months';
import { getLoanAmountError, getLoanPlan, LOAN_MAX, LOAN_MIN, LOAN_MIN_PRINCIPAL, LOAN_RATE } from '@/lib/loans';
import { FIELD_CLASS, LABEL_CLASS, PRIMARY_BUTTON_CLASS, READONLY_FIELD_CLASS } from '@/lib/styles';

interface NewLoanFormProps {
  policy: Policy;
  onSaved: () => void;
}

interface NewLoanValues {
  amount: string;
  givenAs: CashPaymentType;
  agreement: UploadedFile | null;
}

const digitsOnly = (value: string) => value.replace(/\D/g, '').slice(0, 6);

/** Gives a new loan on a policy. The monthly payment field is only a calculator for the admin. */
export function NewLoanForm({ policy, onSaved }: NewLoanFormProps) {
  const [monthlyPayment, setMonthlyPayment] = useState(String(LOAN_MIN_PRINCIPAL));

  const formik = useFormik<NewLoanValues>({
    initialValues: { amount: '', givenAs: 'Cash', agreement: null },
    validate: (values) => {
      const errors: Partial<Record<keyof NewLoanValues, string>> = {};
      const amount = Number(values.amount);
      if (!values.amount) errors.amount = 'Please enter loan amount';
      else if (getLoanAmountError(amount)) errors.amount = getLoanAmountError(amount);
      if (!values.agreement) errors.agreement = 'Please upload the signed loan agreement (PDF)';
      return errors;
    },
    onSubmit: async (values, { setStatus }) => {
      setStatus(undefined);
      try {
        await createLoan(
          LoanAPIRequestMapper({
            policyId: policy.id,
            amount: Number(values.amount),
            givenAs: values.givenAs,
            agreementUrl: values.agreement!.url,
          })
        );
        onSaved();
      } catch (error) {
        setStatus(getApiErrorMessage(error));
      }
    },
  });

  const amount = Number(formik.values.amount) || 0;
  const amountError = getLoanAmountError(amount);
  const monthly = Number(monthlyPayment) || 0;
  const monthlyHint =
    monthly && monthly < LOAN_MIN_PRINCIPAL
      ? `Minimum monthly payment is ${formatCurrency(LOAN_MIN_PRINCIPAL)}`
      : monthly > amount && amount
        ? 'Monthly payment cannot be more than the loan amount'
        : '';
  const plan = amount && !amountError && monthly >= LOAN_MIN_PRINCIPAL && monthly <= amount ? getLoanPlan(amount, monthly) : null;
  const canGive = Boolean(amount) && !amountError && Boolean(formik.values.agreement);

  return (
    <DrawerSection
      icon={Wallet}
      title="New Loan"
      description={`Up to ${formatCurrency(LOAN_MAX)} · first repayment in ${formatMonthKey(addMonthsToKey(currentMonthKey(), 1))}`}
    >
      <form noValidate onSubmit={formik.handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <div className="flex min-h-5 items-center justify-between">
            <Label htmlFor="loan-amount" className={LABEL_CLASS}>
              Loan Amount (₹) *
            </Label>
            <span className="text-xs text-gray-400">
              {formatCurrency(LOAN_MIN)} – {formatCurrency(LOAN_MAX)}
            </span>
          </div>
          <Input
            id="loan-amount"
            inputMode="numeric"
            placeholder={`Min ${formatCurrency(LOAN_MIN)} · Max ${formatCurrency(LOAN_MAX)}`}
            value={formik.values.amount}
            onChange={(e) => formik.setFieldValue('amount', digitsOnly(e.target.value))}
            className={FIELD_CLASS}
          />
          <FieldError message={amountError || (formik.submitCount > 0 ? formik.errors.amount : undefined)} />
        </div>

        <PaymentTypeField
          options={CASH_PAYMENT_TYPES}
          id="loan-given-as"
          label="Given As *"
          cashHint="Handed over in person"
          value={formik.values.givenAs}
          onChange={(value) => formik.setFieldValue('givenAs', value)}
        />

        <PdfUploadField
          label="Loan Agreement (PDF) *"
          caption="Signed loan agreement"
          value={formik.values.agreement}
          onChange={(file) => formik.setFieldValue('agreement', file)}
          error={formik.submitCount > 0 ? (formik.errors.agreement as string | undefined) : undefined}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <div className="flex min-h-5 items-center justify-between">
              <Label htmlFor="loan-monthly" className={LABEL_CLASS}>
                Monthly Payment (₹)
              </Label>
              <span className="text-xs text-gray-400">For calculation only</span>
            </div>
            <Input
              id="loan-monthly"
              inputMode="numeric"
              value={monthlyPayment}
              onChange={(e) => setMonthlyPayment(digitsOnly(e.target.value))}
              className={FIELD_CLASS}
            />
            <FieldError message={monthlyHint} />
          </div>
          <div className="space-y-2">
            <Label className={LABEL_CLASS}>Interest (₹)</Label>
            <Input readOnly value={plan ? formatCurrency(plan.firstInterest) : '--'} className={READONLY_FIELD_CLASS} />
            <p className="text-xs text-gray-500">
              {amount && !amountError ? `First month · ${formatCurrency(amount)} × ${LOAN_RATE * 100}% ÷ 12` : 'Enter loan amount'}
            </p>
          </div>
        </div>

        <dl className="divide-y divide-gray-100 border-t border-gray-100">
          <div className="flex items-start justify-between gap-6 py-3">
            <dt className="text-sm text-gray-500">Total (First Month)</dt>
            <dd className="text-right">
              <span className="text-base font-semibold tabular-nums text-teal-800">{plan ? formatCurrency(plan.firstTotal) : '--'}</span>
              {plan && (
                <span className="block text-xs text-gray-500">
                  Monthly Payment {formatCurrency(monthly)} + Interest {formatCurrency(plan.firstInterest)}
                </span>
              )}
            </dd>
          </div>
          <div className="flex items-start justify-between gap-6 py-3">
            <dt className="text-sm text-gray-500">Months to Close</dt>
            <dd className="text-right text-sm font-medium text-gray-900">
              {plan ? `${plan.months} ${plan.months === 1 ? 'month' : 'months'}` : '--'}
              {plan && <span className="block text-xs font-normal text-gray-500">Closes in {formatMonthKey(plan.closesIn)}</span>}
            </dd>
          </div>
        </dl>

        {formik.status && (
          <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{formik.status}</span>
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={!canGive || formik.isSubmitting} className={`min-w-36 ${PRIMARY_BUTTON_CLASS}`}>
            {formik.isSubmitting ? 'Saving...' : 'Give Loan'}
          </Button>
        </div>
      </form>
    </DrawerSection>
  );
}
